import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

/**
 * Service zum Laden von Tabellendaten aus JSON-Dateien.
 * Lazy Loading mit Caching.
 */
@Injectable({
	providedIn: 'root'
})
export class MdbService {
	private basePath = '/assets/tables';

	/** Cache für geladene Tabellen */
	private tables = new Map<string, any[]>();

	/** Laufende Lade-Promises (verhindert doppeltes Laden) */
	private loadPromises = new Map<string, Promise<any[]>>();

	constructor(private http: HttpClient) {}

	async getTableAsync(name: string): Promise<any[]> {
		if (this.tables.has(name)) {
			return this.tables.get(name)!;
		}

		if (this.loadPromises.has(name)) {
			return this.loadPromises.get(name)!;
		}

		const promise = this.loadTable(name);
		this.loadPromises.set(name, promise);

		try {
			const data = await promise;
			this.tables.set(name, data);
			return data;
		} finally {
			this.loadPromises.delete(name);
		}
	}

	private async loadTable(name: string): Promise<any[]> {
		const url = `${this.basePath}/${name}.json`;
		try {
			const data = await lastValueFrom(this.http.get<any[]>(url));
			return data ?? [];
		} catch (err) {
			console.warn(`[MdbService] Tabelle ${name} konnte nicht geladen werden:`, err);
			return [];
		}
	}

	getTable(name: string): any[] | undefined {
		if (this.tables.has(name)) {
			return this.tables.get(name);
		}
		this.getTableAsync(name);
		return undefined;
	}

	isTableLoaded(name: string): boolean {
		return this.tables.has(name);
	}

	async preloadTables(names: string[]): Promise<void> {
		await Promise.all(names.map(name => this.getTableAsync(name)));
	}
}
